import type React from 'react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  styled,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Button,
  LinearProgress,
  Typography,
  Snackbar,
  Alert,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  type GridCellParams,
  type GridColDef,
  type GridRowParams,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { TimeField } from '@mui/x-date-pickers/TimeField';

import { useAppSelector } from '../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../hooks/useAppDispatch';

import type {
  TripLegAttestationItem,
  TripLegResponseItem,
} from '../../../data/models/trip-leg.model';
import {
  TripLegStatus,
  TripLegStatusBC,
  tripStatusNoLoadList,
} from '../../../data/models/trip-leg-status.model';
import type { PagedList } from '../../../data/models/paged-list.model';

import { selectTimeZones } from '../../../data/reference.slice';
import {
  selectUpdatingTripLegLogStatus,
  selectUpdatingTripLegLogID,
  updateTripLegLogAsync,
  setTripLegError,
  selectTripLegError,
  overrideTripLegStatus,
} from '../../../data/trip.slice';

import { DataGridPro } from '../../tables/DataGridPro';
import { CellDualValue } from '../../tables/CellDualValue';
import { CellEditableField } from '../../tables/CellEditableField';
import { attestationUtils } from '../../../services/attestationUtils';
import { financialUtils } from '../../../services/financialUtils';
import PriceTooltip from '../status/PriceTooltipAsync';

import DateUtils from '../../../services/DateUtils';
import { StatusDialog } from '../../dialogs/StatusDialog';
import { DateTime } from 'luxon';
import { tripLegStatusBCToStatusMap } from '../../../utils/tripLegStatusMap';

const PREFIX = 'AttestTripsTable';

const classes = {
  content: `${PREFIX}-content`,
  gridContainer: `${PREFIX}-gridContainer`,
  grid: `${PREFIX}-grid`,
  closeButton: `${PREFIX}-closeButton`,
  rowHover: `${PREFIX}-rowHover`,
  cellContent: `${PREFIX}-cellContent`,
  primaryText: `${PREFIX}-primaryText`,
  secondaryText: `${PREFIX}-secondaryText`,
  actions: `${PREFIX}-actions`,
  renderTime: `${PREFIX}-renderTime`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.content}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  [`& .${classes.gridContainer}`]: {
    flexGrow: 1,
    boxShadow: '0px 2px 7px 0px rgba(1, 16, 18, 0.15)',
  },
  [`& .${classes.grid}`]: {
    border: 'none',
    fontFamily: 'Source Sans Pro',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '143%',
    letterSpacing: '0.17px',

    '& .MuiDataGrid-virtualScrollerRenderZone': {
      zIndex: 1000,
      paddingTop: 8,
    },

    '& .MuiDataGrid-columnHeaderTitleContainer, & .MuiDataGrid-columnHeaderTitleContent, & .MuiDataGrid-columnHeaderTitleContainerContent, & .MuiDataGrid-columnHeaderTitle':
      {
        width: '100%',
        color: 'rgba(1, 16, 18, 0.90)',

        fontFamily: 'Source Sans Pro',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: '600',
        lineHeight: '24px',
        letterSpacing: '0.17px',
      },

    // '& .MuiDataGrid-row--borderBottom .MuiDataGrid-columnHeader': {
    //   borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    // },
    '& .MuiDataGrid-columnSeparator': {
      display: 'none',
    },

    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },

    '& .MuiDataGrid-cell': {
      '&.MuiDataGrid-cellCheckbox': {
        overflow: 'visible',

        '& .MuiButtonBase-root': {
          padding: '8px',
          marginLeft: '-6px',
          '& .MuiSvgIcon-root': {
            width: '24px !important',
            height: '24px !important',
          },
        },

        '& .MuiCheckbox-root.Mui-disabled': {
          position: 'relative',
          overflow: 'visible',
          pointerEvents: 'auto',
          cursor: 'pointer',
        },

        '& .MuiCheckbox-root.Mui-disabled::before': {
          content: `'Add missing trip info'`,
          position: 'absolute',
          fontFamily: 'Source Sans Pro',
          fontStyle: 'normal',
          fontSize: 10,
          fontWeight: 600,
          lineHeight: '14px',
          bottom: 36,
          left: 8,
          background: 'rgba(1, 16, 18, 0.90)',
          color: 'white',
          padding: '4px 8px',
          borderRadius: 4,
          boxShadow:
            '0px 5px 5px -3px rgba(0, 0, 0, 0.10), 0px 8px 10px 1px rgba(0, 0, 0, 0.07), 0px 3px 14px 2px rgba(0, 0, 0, 0.06)',

          opacity: 0,
          visibility: 'hidden',
          transition: 'opacity 0.3s ease-in-out',
        },

        '& .MuiCheckbox-root.Mui-disabled::after': {
          content: `''`,
          position: 'absolute',
          bottom: 22,
          left: 14,
          borderWidth: 6,
          borderTopWidth: 8,
          borderStyle: 'solid',
          borderColor:
            'rgba(1, 16, 18, 0.90) transparent transparent transparent',
          opacity: 0,
          visibility: 'hidden',
          transition: 'opacity 0.3s ease-in-out',
        },

        '& .MuiCheckbox-root.Mui-disabled:hover::before': {
          opacity: 1,
          visibility: 'visible',
        },
        '& .MuiCheckbox-root.Mui-disabled:hover::after': {
          opacity: 1,
          visibility: 'visible',
        },
      },
    },

    '& .MuiDataGrid-columnHeaderCheckbox': {
      width: '32px !important',

      // '& div': {
      //   display: 'none',
      // },
    },

    '& .MuiDataGrid-cellCheckbox': {
      '--width': '44px !important',
      marginRight: '-10px !important',
    },

    // I'm not sure why this was here but it causes console errors. I can't see any
    // difference with it in or out.
    // '& .MuiDataGrid-cell:nth-last-child(2)': {
    //   flex: 1,
    // },
    // [`& .MuiDataGridPro-row--editing`]: {
    //   [`& .${classes.cellContent} fieldset`]: {
    //     display: 'block !important',
    //   },
    // },
    '.MuiDataGrid-cell.MuiDataGrid-cell--editing:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-row': {
      '& .MuiDataGrid-cell:last-child button': {
        visibility: 'hidden',
      },
      [`& input::placeholder`]: {
        color: 'transparent',
      },
      [`& fieldset`]: {
        display: 'none',
      },
    },
    [`& .MuiDataGrid-row:hover, & .MuiDataGrid-row.${classes.rowHover}`]: {
      '& .MuiDataGrid-cell:last-child button': {
        visibility: 'visible',
      },
      [`& input::placeholder`]: {
        color: 'rgba(1, 16, 18, 0.60)',
      },
      [`& fieldset`]: {
        display: 'inherit',
      },
    },
    '.MuiDataGrid-cell.MuiDataGrid-cell--editing': {
      boxShadow: 'none',
      // backgroundColor: '#fff',
      '& fieldset': {
        display: 'block',
      },
    },
    // [`& .MuiDataGrid-row:not(:hover):not(.${classes.rowHover})`]: {
    //   '& .MuiDataGrid-cell:last-child button': {
    //     visibility: 'hidden',
    //   },

    //   [`& input::placeholder`]: {
    //     color: 'transparent',
    //   },
    //   [`& fieldset`]: {
    //     display: 'none',
    //   },
    // },
    [`& .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row.${classes.rowHover}, & .MuiDataGrid-row:hover`]:
      {
        '& .MuiDataGrid-row:focus-within': {
          [`& input::placeholder`]: {
            color: 'rgba(1, 16, 18, 0.60)',
            opacity: 1,
          },
          [`& fieldset`]: {
            display: 'inherit',
          },
        },
      },
    [`& .MuiDataGrid-row.${classes.rowHover}, & .MuiDataGrid-row:hover`]: {
      background:
        'linear-gradient(0deg, rgba(1, 16, 18, 0.04) 0%, rgba(1, 16, 18, 0.04) 100%), #FFF',
      ['& .MuiFormControl-root']: {
        backgroundColor: '#fff',
      },
    },
  },
  [`& .${classes.cellContent}`]: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    height: '100%',
    padding: theme.spacing(1),
  },
  [`& .${classes.primaryText}`]: {
    color: 'rgba(1, 16, 18, 0.90)',
    fontFamily: 'Source Sans Pro',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '143%',
    letterSpacing: '0.17px',
  },
  [`& .${classes.secondaryText}`]: {
    color: 'rgba(1, 16, 18, 0.60)',
    fontFamily: 'Source Sans Pro',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '143%',
  },

  [`& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-input`]:
    {
      fontSize: '12px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: '166%',
      letterSpacing: '0.4px',
    },

  [`& .MuiTablePagination-selectLabel`]: {
    color: 'rgba(1, 16, 18, 0.60)',
  },
  [`& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows`]: {
    fontFamily: 'Source Sans Pro',
  },
  [`& .MuiTablePagination-input`]: {
    fontFamily: 'Roboto',

    [`& .MuiSelect-select`]: {
      paddingBottom: 0,
    },
  },
  [`& .${classes.renderTime}`]: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    height: theme.spacing(4),
    fontFamily: 'Source Sans Pro',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '143%',
    textAlign: 'center !important',
    width: '100%',
    '& input': {
      paddingLeft: 0,
      color: 'rgba(1, 16, 18, 0.90)',
      letterSpacing: '0.17px',
      textAlign: 'center',
      padding: '6px',
    },
    '& fieldset': {
      display: 'block',
    },
  },
}));

const StyledMenu = styled(Menu)(({ theme }) => ({
  [`& .MuiPaper-root`]: {
    width: '160px',
    marginLeft: '10px',
    borderRadius: '3px',
    boxShadow:
      '0px 5px 5px -3px rgba(0, 0, 0, 0.10), 0px 8px 10px 1px rgba(0, 0, 0, 0.07), 0px 3px 14px 2px rgba(0, 0, 0, 0.06)',

    [`& .MuiList-root`]: {
      padding: 0,
    },
    [`& .MuiMenuItem-root`]: {
      color: 'rgba(1, 16, 18, 0.60)',
      fontFamily: 'Source Sans Pro',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: '143%',
      letterSpacing: '0.17px',
      padding: '8px 16px',
    },
  },
  [`& .${classes.actions}`]: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.background.paper,
    gap: theme.spacing(2),
    padding: theme.spacing(1),
    alignItems: 'flex-start',
  },
}));

interface AttestTripsTableProps {
  attestableTripLegs: PagedList<TripLegResponseItem> | null;
  onTripsUpdated: (tripLegs: TripLegResponseItem[]) => void;
  onSubmit: () => void;
  loading: boolean;
  providerId: number | null | undefined;
}

const AttestTripsTable = ({
  attestableTripLegs,
  onTripsUpdated,
  onSubmit,
  loading,
  providerId,
}: AttestTripsTableProps) => {
  const { t } = useTranslation('common');
  const apiRef = useGridApiRef();
  const dispatch = useAppDispatch();

  const [moreActionsAnchorEl, setMoreActionsAnchorEl] =
    useState<null | HTMLElement>(null);
  const [moreActionsMenuOpenRowId, setMoreActionsMenuOpenRowId] = useState<
    string | null
  >(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [originalStatus, setOriginalStatus] = useState<TripLegStatusBC | null>(
    null,
  );
  const [targetStatus, setTargetStatus] = useState<TripLegStatus>(
    TripLegStatus.CanceledLate,
  );
  const [tripLegs, setTripLegs] = useState<TripLegAttestationItem[]>([]);
  const [selectedTrips, setSelectedTrips] = useState<string[]>([]);
  const [cellResetCounters, setCellResetCounters] = useState<
    Map<string, number>
  >(new Map());
  const [selectedTripLeg, setSelectedTripLeg] =
    useState<TripLegAttestationItem | null>(null);

  const timezones = useAppSelector(selectTimeZones);
  const updatingTripLegLogID = useAppSelector(selectUpdatingTripLegLogID);
  const updatingTripLegLogStatus = useAppSelector(
    selectUpdatingTripLegLogStatus,
  );
  const tripLegError = useAppSelector(selectTripLegError);

  const updateTripsWithTimestamp = (
    tripLegReferenceID: string,
    status: TripLegStatusBC,
    time: string,
    field: string,
  ) => {
    const newFilteredTrips = tripLegs.map((tripLeg) => {
      const trip = {
        ...tripLeg,
        tripLeg: {
          ...tripLeg,
          [field]: time,
        },
      };
      return trip;
    });
    setTripLegs(newFilteredTrips);
  };

  const isUpdatingCurrentTimeField = (
    tripLegReferenceID: string,
    field: string,
  ): boolean => {
    if (updatingTripLegLogID === tripLegReferenceID) {
      if (
        field === 'onSite' &&
        updatingTripLegLogStatus === TripLegStatusBC.Onsite
      ) {
        return true;
      }
      if (
        field === 'onBoard' &&
        updatingTripLegLogStatus === TripLegStatusBC.Onboard
      ) {
        return true;
      }
      if (
        field === 'completed' &&
        updatingTripLegLogStatus === TripLegStatusBC.Completed
      ) {
        return true;
      }
    }
    return false;
  };

  const checkTimestampsFilled = (tripLeg: TripLegAttestationItem) => {
    const status = tripLeg?.tripLegStatusBC;

    if (
      status === TripLegStatusBC.CanceledLate ||
      status === TripLegStatusBC.NoLoad ||
      status === TripLegStatusBC.Completed
    ) {
      return true;
    }

    return false;
  };

  const getException = (tripLeg: TripLegResponseItem) => {
    const updatedTripLeg = tripLegs.find(
      (tl) => tl.tripLegReferenceID === tripLeg.tripLegReferenceID,
    );

    if (updatedTripLeg?.isUnderDiscussion) {
      return t('needsAttention.underDiscussion');
    }

    if (!isRowValid(updatedTripLeg)) {
      return t('needsAttention.missingInfo');
    }

    return t('needsAttention.readyToInvoice');
  };

  const openMoreActionsMenu = (
    event: React.MouseEvent<HTMLButtonElement>,
    rowId: string,
  ) => {
    setMoreActionsAnchorEl(event.currentTarget);
    setMoreActionsMenuOpenRowId(rowId);
  };

  const closeMoreActionsMenu = () => {
    setMoreActionsAnchorEl(null);
    setMoreActionsMenuOpenRowId(null);
  };

  const renderPickupTime = (params: GridCellParams): React.ReactElement => {
    const { row } = params;
    return (
      <CellDualValue
        primaryValue={row.pickupTime}
        secondaryValue={row.tripID}
      />
    );
  };

  const renderPickupAddress = (params: GridCellParams): React.ReactElement => {
    const { row } = params;

    return (
      <CellDualValue
        primaryValue={row.pickupAddress}
        secondaryValue={`${row.pickupCity}, ${row.pickupState}, ${row.pickupZipCode}`}
      />
    );
  };

  const renderDropoffAddress = (params: GridCellParams): React.ReactElement => {
    const { row } = params;
    return (
      <CellDualValue
        primaryValue={row.dropoffAddress}
        secondaryValue={`${row.dropoffCity}, ${row.dropoffState}, ${row.dropoffZipCode}`}
      />
    );
  };

  const renderDriver = (params: GridCellParams): React.ReactElement => {
    const tripLeg = params.row as TripLegAttestationItem;
    const driverName = tripLeg.driverName || '';
    return (
      <CellEditableField
        value={driverName}
        placeholder="Driver"
        defaultValue={driverName}
        hasFocus={params.hasFocus}
        align="left"
        onChange={(value) => {
          setTripLegs(
            tripLegs.map((current) => {
              if (current.tripLegID === tripLeg.tripLegID) {
                return {
                  ...current,
                  driverName: typeof value === 'string' ? value : undefined,
                };
              }
              return current;
            }),
          );
        }}
      />
    );
  };

  const renderTime = (params: GridCellParams): React.ReactElement => {
    const { row, field } = params;
    const time = params.value
      ? DateTime.fromISO(params.value as string, {
          zone: params.row.timezone,
        })
      : undefined;
    const canceledLateTime = row.canceledLateTimeStamp
      ? DateTime.fromISO(row.canceledLateTimeStamp, {
          zone: params.row.timezone,
        })
      : undefined;
    const noLoadTime = row.noLoadTimeStamp
      ? DateTime.fromISO(row.noLoadTimeStamp, {
          zone: params.row.timezone,
        })
      : undefined;
    const tripLegStatus = row.tripLegStatusBC;

    const getLabel = (): string | null => {
      if (
        tripLegStatus === TripLegStatusBC.NoLoad &&
        noLoadTime &&
        field === 'onBoard'
      ) {
        return t('tripLegStatus.noLoad');
      }
      if (
        tripLegStatus === TripLegStatusBC.CanceledLate &&
        canceledLateTime &&
        field === 'onSite'
      ) {
        return t('tripLegStatus.canceledLate');
      }
      return null;
    };

    if (
      field === 'completed' &&
      (tripLegStatus === TripLegStatusBC.CanceledLate ||
        tripLegStatus === TripLegStatusBC.NoLoad)
    ) {
      return <></>;
    }

    if (field === 'onBoard' && tripLegStatus === TripLegStatusBC.CanceledLate) {
      return <></>;
    }

    const isUpdating = isUpdatingCurrentTimeField(
      params.row.tripLegReferenceID,
      field,
    );

    const label = getLabel();

    let valueToDisplay: DateTime | undefined;

    if (
      field === 'onSite' &&
      tripLegStatus === TripLegStatusBC.CanceledLate &&
      canceledLateTime
    ) {
      valueToDisplay = canceledLateTime;
    } else if (
      field === 'onBoard' &&
      tripLegStatus === TripLegStatusBC.NoLoad
    ) {
      valueToDisplay = noLoadTime;
    } else {
      valueToDisplay = time;
    }
    return (
      <Box className={classes.cellContent}>
        <Typography className={classes.primaryText}>
          {label && (
            <Typography className={classes.primaryText} sx={{ marginTop: 1 }}>
              {label}
            </Typography>
          )}
        </Typography>
        <Typography
          className={`${classes.renderTime} ${label ? 'has-label' : ''}`}>
          <TimeField
            key={`${params.row.tripLegReferenceID}-${field}-${
              cellResetCounters.get(
                `${params.row.tripLegReferenceID}-${field}`,
              ) ?? 0
            }`}
            value={valueToDisplay}
            onBlur={(event) => {
              const newTime = event.target.value;
              const isPlaceholder = newTime === 'hh:mm aa';
              const hasTimeEntered = newTime && !isPlaceholder;

              // Skip update if value hasn't changed
              const currentFormatted = valueToDisplay
                ? valueToDisplay.toFormat('hh:mm a')
                : '';
              if (
                newTime === currentFormatted ||
                (!hasTimeEntered && !valueToDisplay)
              ) {
                return;
              }

              // Clear any existing error when the user enters a new time
              if (hasTimeEntered) {
                dispatch(setTripLegError(null));
              }

              const resetField = () => {
                setCellResetCounters((prev) => {
                  const next = new Map(prev);
                  const cellKey = `${params.row.tripLegReferenceID}-${field}`;
                  next.set(cellKey, (prev.get(cellKey) ?? 0) + 1);
                  return next;
                });
              };

              // Validate that previous status is filled before allowing update
              // Only show error if user actually entered a time
              if (hasTimeEntered && field === 'onBoard' && row.onSite === '') {
                resetField();
                dispatch(
                  setTripLegError(t('needsAttention.onSiteRequiredFirst')),
                );
                return;
              }
              if (
                hasTimeEntered &&
                field === 'completed' &&
                (row.onSite === '' || row.onBoard === '')
              ) {
                resetField();
                dispatch(
                  setTripLegError(
                    t('needsAttention.previousTimestampsRequired'),
                  ),
                );
                return;
              }

              // Validate chronological order for regular trips
              if (
                hasTimeEntered &&
                tripLegStatus !== TripLegStatusBC.NoLoad &&
                tripLegStatus !== TripLegStatusBC.CanceledLate
              ) {
                const date = params.row.appointmentDate;
                const zone = params.row.timezone;
                const newDateTime = DateTime.fromFormat(
                  `${date} ${newTime}`,
                  'yyyy-MM-dd h:mm a',
                  { zone },
                );

                if (field === 'onBoard' && row.onSite) {
                  const onSiteDateTime = DateTime.fromISO(row.onSite, {
                    zone,
                  });
                  if (
                    newDateTime.isValid &&
                    onSiteDateTime.isValid &&
                    newDateTime < onSiteDateTime
                  ) {
                    resetField();
                    dispatch(
                      setTripLegError(t('needsAttention.onBoardBeforeOnSite')),
                    );
                    return;
                  }
                }

                if (field === 'completed' && row.onBoard) {
                  const onBoardDateTime = DateTime.fromISO(row.onBoard, {
                    zone,
                  });
                  if (
                    newDateTime.isValid &&
                    onBoardDateTime.isValid &&
                    newDateTime < onBoardDateTime
                  ) {
                    resetField();
                    dispatch(
                      setTripLegError(
                        t('needsAttention.completedBeforeOnBoard'),
                      ),
                    );
                    return;
                  }
                }
              }

              // Validate NoLoad time is not earlier than OnSite time
              if (hasTimeEntered && tripLegStatus === TripLegStatusBC.NoLoad) {
                const date = params.row.appointmentDate;
                const zone = params.row.timezone;
                const newDateTime = DateTime.fromFormat(
                  `${date} ${newTime}`,
                  'yyyy-MM-dd h:mm a',
                  { zone },
                );

                if (field === 'onBoard' && row.onSite) {
                  const onSiteDateTime = DateTime.fromISO(row.onSite, {
                    zone,
                  });
                  if (
                    newDateTime.isValid &&
                    onSiteDateTime.isValid &&
                    newDateTime <= onSiteDateTime
                  ) {
                    resetField();
                    dispatch(
                      setTripLegError(
                        t('needsAttention.noLoadTimeBeforeOnSite'),
                      ),
                    );
                    return;
                  }
                }

                if (field === 'onSite' && noLoadTime) {
                  if (newDateTime.isValid && newDateTime >= noLoadTime) {
                    resetField();
                    dispatch(
                      setTripLegError(
                        t('needsAttention.noLoadTimeBeforeOnSite'),
                      ),
                    );
                    return;
                  }
                }
              }

              const date = params.row.appointmentDate;
              let fieldName = params.field.toUpperCase();
              if (field === 'onSite' && canceledLateTime) {
                fieldName =
                  TripLegStatusBC.CanceledLate.toString().toUpperCase();
              }
              if (
                field === 'onBoard' &&
                (noLoadTime || tripLegStatus === TripLegStatusBC.NoLoad)
              ) {
                fieldName = TripLegStatusBC.NoLoad.toString().toUpperCase();
              }
              const newDateTime = DateTime.fromFormat(
                `${date} ${newTime}`,
                'yyyy-MM-dd h:mm a',
                { zone: params.row.timezone },
              ).toUTC();
              const isSame =
                newDateTime.startOf('minute').toISO() ===
                valueToDisplay?.startOf('minute').toISO();
              if (newDateTime.isValid && !isSame && !isPlaceholder) {
                updateTripsWithTimestamp(
                  params.row.tripLegReferenceID,
                  fieldName as TripLegStatusBC,
                  event.target.value,
                  field,
                );
                const timestampExists = attestableTripLegs?.items.find(
                  (tl) =>
                    tl.tripLegReferenceID === params.row.tripLegReferenceID &&
                    tl.tripLegStatusLogs?.find(
                      (log) => log.tripLegStatusBC === fieldName,
                    ),
                )
                  ? true
                  : false;

                if (fieldName === TripLegStatusBC.Completed) {
                  dispatch(
                    overrideTripLegStatus(
                      params.row.passengerUUID,
                      params.row.tripLegReferenceID,
                      tripLegStatusBCToStatusMap[fieldName as TripLegStatusBC]!,
                      newDateTime.toISO(),
                      providerId,
                    ),
                  );
                }

                dispatch(
                  updateTripLegLogAsync(
                    fieldName as TripLegStatusBC,
                    params.row.passengerUUID,
                    params.row.tripLegReferenceID,
                    newDateTime.setZone('utc').toISO()!,
                    providerId,
                    timestampExists,
                    params.row.tripLegStatusBC,
                  ),
                );
              }
            }}
          />
        </Typography>
        {isUpdating && <LinearProgress />}
      </Box>
    );
  };

  const renderException = (params: GridCellParams): React.ReactElement => {
    const { row } = params;
    return <Box className={classes.cellContent}>{getException(row)}</Box>;
  };

  const renderCenterHeader = ({
    colDef: { headerName },
  }: {
    colDef: { headerName?: string };
  }): React.ReactNode => {
    return (
      <Box
        style={{
          textAlign: 'center',
          width: '100%',
          lineHeight: '100%',
          height: '1',
        }}>
        {headerName}
      </Box>
    );
  };

  const renderPrice = (params: GridCellParams): React.ReactElement => {
    return <PriceTooltip params={params as any} classes={classes} />;
  };

  const isRowValid = (tripLeg: TripLegAttestationItem | undefined) => {
    if (!tripLeg) return false;
    // Look up current state to get the latest driverName
    const updatedTripLeg = tripLegs.find(
      (tl) => tl.tripLegReferenceID === tripLeg.tripLegReferenceID,
    );
    if (!updatedTripLeg) return false;

    const isValidDriver = Boolean(updatedTripLeg.driverName);
    const isValidPayableAmount = Boolean(updatedTripLeg.payableAmount);
    const isValidCanceledLate =
      updatedTripLeg.tripLegStatusBC === TripLegStatusBC.CanceledLate;
    const isValidNoLoad =
      updatedTripLeg.tripLegStatusBC === TripLegStatusBC.NoLoad;
    const isValidComplete =
      updatedTripLeg.tripLegStatusBC === TripLegStatusBC.Completed;
    return (
      isValidDriver &&
      isValidPayableAmount &&
      (isValidCanceledLate || isValidNoLoad || isValidComplete)
    );
  };

  const isRowSelectable = (params: GridRowParams<TripLegAttestationItem>) => {
    const { row } = params;
    return isRowValid(row);
  };

  const columns: GridColDef<TripLegAttestationItem>[] = [
    {
      field: 'pickupTime',
      headerName: t('needsAttention.pickup'),
      flex: 1,
      renderCell: renderPickupTime,
    },
    {
      field: 'pickupAddress',
      headerName: t('needsAttention.pickupAddress'),
      flex: 1,
      renderCell: renderPickupAddress,
    },
    {
      field: 'dropoffAddress',
      headerName: t('needsAttention.dropoffAddress'),
      flex: 1,
      renderCell: renderDropoffAddress,
    },
    {
      field: 'driverName',
      headerName: t('needsAttention.driver'),
      width: 140,
      editable: true,
      renderEditCell: renderDriver,
      renderCell: renderDriver,
    },
    {
      field: 'onSite',
      headerName: t('tripLegStatus.onSite'),
      renderCell: renderTime,
      renderEditCell: renderTime,
      width: 120,
      renderHeader: (params) => renderCenterHeader(params),
      sortable: false,
      editable: true,
    },
    {
      field: 'onBoard',
      headerName: t('tripLegStatus.onBoard'),
      renderCell: renderTime,
      renderEditCell: renderTime,
      width: 120,
      align: 'center',
      renderHeader: (params) => renderCenterHeader(params),
      sortable: false,
      editable: true,
    },
    {
      field: 'completed',
      headerName: t('tripLegStatus.completed'),
      renderCell: renderTime,
      renderEditCell: renderTime,
      width: 120,
      align: 'center',
      renderHeader: (params) => renderCenterHeader(params),
      sortable: false,
      editable: true,
    },
    {
      field: 'price',
      headerName: t('needsAttention.price'),
      width: 70,
      renderCell: renderPrice,
    },
    {
      field: 'exception',
      headerName: t('needsAttention.exception'),
      width: 120,
      renderCell: renderException,
    },
    {
      field: 'actions',
      headerName: ' ',
      width: 40,
      sortable: false,
      renderCell: (params) => (
        <Box className={classes.cellContent}>
          <IconButton
            onClick={(e) =>
              openMoreActionsMenu(e, params.row.tripLegReferenceID)
            }>
            <MoreVertIcon />
          </IconButton>
          <StyledMenu
            anchorEl={moreActionsAnchorEl}
            open={
              Boolean(moreActionsAnchorEl) &&
              moreActionsMenuOpenRowId === params.row.tripLegReferenceID
            }
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            onClose={closeMoreActionsMenu}
            elevation={1}>
            <MenuItem
              disabled={params.row.tripLegStatusBC === TripLegStatusBC.NoLoad}
              onClick={() => {
                setSelectedTripLeg(params.row);
                setOriginalStatus(
                  params.row.tripLegStatusBC as TripLegStatusBC,
                );
                setTargetStatus(TripLegStatus.NoLoad);
                setStatusDialogOpen(true);
                closeMoreActionsMenu();
              }}>
              {t('tripLegStatus.noLoad')}
            </MenuItem>
            <MenuItem
              disabled={
                params.row.tripLegStatusBC === TripLegStatusBC.CanceledLate
              }
              onClick={() => {
                setSelectedTripLeg(params.row);
                setOriginalStatus(
                  params.row.tripLegStatusBC as TripLegStatusBC,
                );
                setTargetStatus(TripLegStatus.CanceledLate);
                setStatusDialogOpen(true);
                closeMoreActionsMenu();
              }}>
              {t('tripLegStatus.canceledLate')}
            </MenuItem>
            <MenuItem
              disabled={
                params.row.tripLegStatusBC !== TripLegStatusBC.CanceledLate &&
                params.row.tripLegStatusBC !== TripLegStatusBC.NoLoad
              }
              onClick={() => {
                setSelectedTripLeg(params.row);
                setOriginalStatus(
                  params.row.tripLegStatusBC as TripLegStatusBC,
                );
                setTargetStatus(TripLegStatus.Completed);
                setStatusDialogOpen(true);
                closeMoreActionsMenu();
              }}>
              {t('tripLegStatus.completed')}
            </MenuItem>
          </StyledMenu>
        </Box>
      ),
    },
  ];

  const handleSubmit = () => {
    const selectedTripLegs = tripLegs.filter((tripLeg) => {
      if (tripLeg.tripLegReferenceID) {
        return selectedTrips.includes(tripLeg.tripLegReferenceID);
      }
      return false;
    });
    onTripsUpdated(selectedTripLegs);
    if (selectedTripLegs.length > 0) {
      onSubmit();
    }
  };

  useEffect(() => {
    // Filter out Will Call trips that haven't been activated (no pickup time)
    const filteredItems =
      attestableTripLegs?.items.filter(
        (tripLeg) => !(tripLeg.isWillCall && !tripLeg.pickUpDueTimeUTC),
      ) || [];

    const legs: TripLegAttestationItem[] = filteredItems.map(
      (tripLeg: TripLegResponseItem): TripLegAttestationItem => {
        const sortedLogs = [...(tripLeg.tripLegStatusLogs || [])].sort(
          (a, b) =>
            new Date(b.createdOnUTC).getTime() -
            new Date(a.createdOnUTC).getTime(),
        );

        const timezone = DateUtils.getTimeZoneIana(
          timezones,
          tripLeg?.pickUpAddressTimeZoneID ?? 0,
        );

        const baseAmount = financialUtils.parsePrice(
          (tripLeg?.payableAmount as string) ?? '0',
        );
        const addOnTotal =
          tripLeg.tripLegAddOnLineItems?.reduce((sum, addOn) => {
            return sum + financialUtils.parsePrice(addOn.lineItemAmount);
          }, 0) || 0;
        const totalPrice = financialUtils.formatPrice(baseAmount + addOnTotal);

        const updatedTripLeg = {
          ...tripLeg,
          tripLegStatusBC: tripLeg?.tripLegStatusBC as TripLegStatusBC,
          id: tripLeg?.tripLegID ?? 0,
          tripID: tripLeg.tripLegReferenceID,
          pickupTime: attestationUtils.formatDate(
            tripLeg?.pickUpDueTimeUTC,
            timezone,
            false,
          ),
          pickupAddress: [
            tripLeg?.pickUpAddressLine1,
            tripLeg?.pickUpAddressLine2,
          ]
            .filter(Boolean)
            .join(', '),
          pickupCity: tripLeg?.pickUpCity,
          pickupState: tripLeg?.pickUpStateCode,
          pickupZipCode: tripLeg?.pickUpZipCode,
          dropoffAddress: [
            tripLeg?.dropOffAddressLine1,
            tripLeg?.dropOffAddressLine2,
          ]
            .filter(Boolean)
            .join(', '),
          dropoffCity: tripLeg?.dropOffCity,
          dropoffState: tripLeg?.dropOffStateCode,
          dropoffZipCode: tripLeg?.dropOffZipCode,
          timezone: timezone,
          onSite: sortedLogs.find(
            (log) => log.tripLegStatusBC === TripLegStatusBC.Onsite,
          )?.createdOnUTC,
          onBoard: sortedLogs.find(
            (log) => log.tripLegStatusBC === TripLegStatusBC.Onboard,
          )?.createdOnUTC,
          completed: sortedLogs.find(
            (log) => log.tripLegStatusBC === TripLegStatusBC.Completed,
          )?.createdOnUTC,
          noLoadTimeStamp: sortedLogs.find((log) =>
            tripStatusNoLoadList.includes(
              log.tripLegStatusBC as TripLegStatusBC,
            ),
          )?.createdOnUTC,
          canceledLateTimeStamp: sortedLogs.find(
            (log) => log.tripLegStatusBC === TripLegStatusBC.CanceledLate,
          )?.createdOnUTC,
          driverName: attestationUtils.getDriverName(tripLeg),
          price: totalPrice,
          status: tripLeg?.tripLegStatusBC,
          exception: '', // Will be set after preserving driver name
          hasRequiredTimestamps: false,
        };
        // Driver is the only field not updated on the BE, so we need to update it here
        // from the previous user entry so that API fetches do not clear the field
        const currentDriver = tripLegs.find(
          (tl) => tl.tripLegReferenceID === tripLeg.tripLegReferenceID,
        )?.driverName;
        if (currentDriver) {
          updatedTripLeg.driverName = currentDriver;
        }
        // Set hasRequiredTimestamps after all timestamps are set
        updatedTripLeg.hasRequiredTimestamps =
          checkTimestampsFilled(updatedTripLeg);
        // Calculate exception after restoring driver name to ensure it's accurate
        updatedTripLeg.exception = getException(updatedTripLeg);
        return updatedTripLeg;
      },
    );
    setTripLegs(legs);
  }, [attestableTripLegs]);

  return (
    <StyledBox className={classes.content}>
      <Paper elevation={3} className={classes.gridContainer}>
        <DataGridPro
          getRowId={(row) => row.tripLegReferenceID}
          rows={tripLegs}
          columns={columns}
          apiRef={apiRef}
          getRowClassName={(params) => {
            if (params.row.tripLegReferenceID === moreActionsMenuOpenRowId) {
              return classes.rowHover;
            }
            return '';
          }}
          className={classes.grid}
          checkboxSelection
          isRowSelectable={isRowSelectable}
          disableRowSelectionOnClick
          rowSelectionModel={selectedTrips}
          onRowSelectionModelChange={(model) => {
            setSelectedTrips(model as string[]);
          }}
          hideFooterSelectedRowCount
          rowHeight={56}
          disableColumnMenu={true}
          disableColumnSorting={true}
          loading={loading}
          // editMode="row"
          // hideFooter
          // cellModesModel={cellModesModel}
          // onCellModesModelChange={handleCellModesModelChange}
          // onCellClick={handleCellClick}
        />
      </Paper>
      <Box className={classes.actions}>
        <Button
          variant="contained"
          color="primary"
          fullWidth={false}
          disabled={!selectedTrips.length}
          onClick={handleSubmit}>
          {t('common.submit')}
        </Button>
      </Box>
      <StatusDialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        selectedTripLeg={selectedTripLeg}
        providerId={providerId ?? 0}
        originalStatus={originalStatus ?? null}
        targetStatus={targetStatus}
        onStatusChanged={(tripLegReferenceID, newStatus, timestamps) => {
          setTripLegs(
            tripLegs.map((tripLeg) => {
              if (tripLeg.tripLegReferenceID !== tripLegReferenceID) {
                return tripLeg;
              }
              const updated = {
                ...tripLeg,
                tripLegStatusBC: newStatus,
                ...(timestamps.onSite && { onSite: timestamps.onSite }),
                ...(timestamps.onBoard && { onBoard: timestamps.onBoard }),
                ...(timestamps.completed && {
                  completed: timestamps.completed,
                }),
                ...(timestamps.canceledLate && {
                  canceledLateTimeStamp: timestamps.canceledLate,
                }),
              };
              updated.hasRequiredTimestamps = checkTimestampsFilled(updated);
              updated.exception = getException(updated);
              return updated;
            }),
          );
        }}
      />
      <Snackbar
        open={!!tripLegError}
        onClose={() => dispatch(setTripLegError(null))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert variant="filled" severity="error">
          {tripLegError}
        </Alert>
      </Snackbar>
    </StyledBox>
  );
};

export { AttestTripsTable };
